import { Random } from '../Random.js';

/**
 * MelodicSyntax.js
 * Implements Classical formal structures for melody generation.
 * Moves beyond "random walk" to "Sentence" and "Period" forms.
 */

export class MelodicSyntax {
    constructor(random) {
        this.random = random || new Random();
    }

    /**
     * Generates a "Sentence" structure (typically 8 bars).
     * Format: Presentation (2b) -> Repetition (2b) -> Fragmentation (2b) -> Cadence (2b)
     * @param {Object} context - The generation context (chords, scale, etc.)
     * @param {Array} motif - The base rhythmic/melodic motif
     */
    generateSentence(context, motif) {
        const { barStart, barIndex, harmony, chord } = context;

        // Calculate relative position in the 8-bar phrase
        // Assuming standard 4/4, 8-bar sentence.
        const phraseLength = 8;
        const localBar = Math.floor(barIndex) % phraseLength;

        // 1. Presentation (Bars 0-1)
        if (localBar < 2) {
            return this.generatePresentation(context, motif);
        }
        // 2. Repetition (Bars 2-3)
        else if (localBar < 4) {
            return this.generateRepetition(context, motif);
        }
        // 3. Fragmentation (Bars 4-5)
        else if (localBar < 6) {
            return this.generateFragmentation(context, motif);
        }
        // 4. Cadence (Bars 6-7)
        else {
            return this.generateCadence(context);
        }
    }

    /**
     * Generates a "Period" structure.
     * Antecedent (Question) -> Consequent (Answer)
     */
    generatePeriod(context, motif) {
        const phraseLength = 8;
        const localBar = Math.floor(context.barIndex) % phraseLength;

        // Antecedent (Bars 0-3)
        if (localBar < 4) {
            if (localBar === 3) return this.generateCadence(context, 'weak'); // Half Cadence
            return this.generatePresentation(context, motif);
        }
        // Consequent (Bars 4-7)
        else {
            if (localBar === 7) return this.generateCadence(context, 'strong'); // PAC
            // Slight variation of presentation
            return this.generateRepetition(context, motif, 'variation');
        }
    }

    generatePresentation(context, motif) {
        // Just play the motif adapted to current harmony
        return this.adaptToHarmony(motif, context.chord);
    }

    generateRepetition(context, motif, type = 'exact') {
        let variedMotif = [...motif];

        if (type === 'variation' || this.random.bool(0.3)) {
            // Slight inversion or step change
            variedMotif = variedMotif.map(n => ({
                ...n,
                scaleDegree: n.scaleDegree + (this.random.bool() ? 1 : -1)
            }));
        } else {
            // Sequence: Transpose motif to new chord level
            // (Handled automatically by adaptToHarmony usually, but we can force offset)
        }

        return this.adaptToHarmony(variedMotif, context.chord);
    }

    generateFragmentation(context, motif) {
        // Shorten the motif (take first half) and double it
        const fragmentLen = Math.max(1, Math.floor(motif.length / 2));
        const fragment = motif.slice(0, fragmentLen);

        // Double tempo feel? Or just repeat fragment twice in the bar?
        // Let's repeat fragment
        const repeatedFragment = [
            ...fragment,
            ...fragment.map(n => ({ ...n, s: n.s + 2.0 })) // Shift 2 beats (half bar)
        ];

        return this.adaptToHarmony(repeatedFragment, context.chord);
    }

    generateCadence(context, type = 'any') {
        // Simple long notes or resolution logic
        // Check for chord presence
        if (!context.chord) return [];

        const root = context.chord.rootPitchClass;
        const notes = [];

        // Target note: Root for strong cadence, 3rd/5th for weak
        let targetDegree = 0; // Root relative to chord
        if (type === 'weak') targetDegree = 2; // 3rd (0-indexed scale degree approximation)

        notes.push({
            s: context.barStart,
            l: 2.0, // Long note
            scaleDegree: targetDegree,
            isChordTone: true
        });

        return this.adaptToHarmony(notes, context.chord);
    }

    /**
     * Applies Apex Logic to a generated phrase.
     * Finds the highest note and boosts velocity/duration if it falls in the "Golden Ratio" zone.
     */
    applyApexLogic(events) {
        if (!events || events.length === 0) return events;

        // Sort by time to find phrase structure
        const sorted = [...events].sort((a, b) => a.s - b.s);
        const start = sorted[0].s;
        const end = sorted[sorted.length-1].s + (sorted[sorted.length-1].len || 0);
        const duration = end - start;

        // Golden Ratio point ~0.618
        const apexZoneStart = start + (duration * 0.5);
        const apexZoneEnd = start + (duration * 0.8);

        // Find candidate for Apex (highest pitch in the zone)
        let apexNote = null;
        let maxPitch = -1;

        sorted.forEach(ev => {
            if (ev.s >= apexZoneStart && ev.s <= apexZoneEnd) {
                if (ev.d > maxPitch) {
                    maxPitch = ev.d;
                    apexNote = ev;
                }
            }
        });

        if (apexNote) {
            return events.map(ev => {
                if (ev === apexNote) {
                    return {
                        ...ev,
                        vel: Math.min(127, (ev.vel || 80) * 1.2), // Accent
                        articulation: 'accent'
                    };
                }
                // Dip others slightly for contrast?
                return ev;
            });
        }

        return events;
    }

    // Helper: Map scale degrees/offsets to actual pitches based on chord
    adaptToHarmony(motifEvents, chord) {
        if (!chord) return [];

        return motifEvents.map(m => {
            // Rough mapping: chord.notes[m.scaleDegree % chord.notes.length]
            // This is a simplified fallback; actual MelodyStrategies usually handles this.
            // We return events with 'scaleDegree' so the caller can map them.
            // If caller expects pitches, we must generate them.

            // Assume motif has { s, l, scaleDegree }
            // chord has { rootPitchClass, notes: [...] }

            let pitch = chord.rootPitchClass + 60; // Default middle C area

            if (chord.notes && chord.notes.length > 0) {
                const degree = m.scaleDegree || 0;
                const len = chord.notes.length;

                // Fix: "Infinite Root Note" - Preserve Octave and Direction
                // Wrap index correctly for negative numbers
                const wrappedIndex = ((degree % len) + len) % len;

                // Calculate octave shift
                const octaveShift = Math.floor(degree / len);

                // Map to pitch
                pitch = chord.notes[wrappedIndex] + (octaveShift * 12);

                // Safety Clamp: Keep within MIDI range (0-127)
                pitch = Math.max(0, Math.min(127, pitch));
            }

            return {
                s: m.s, // Relative to bar? Caller needs to adjust absolute time.
                len: m.l,
                d: pitch,
                vel: 0.8
            };
        });
    }
}
